/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/edit/fillet 层；
 * 2. 负责把圆角特征原型与宿主编辑会话互相投影；
 * 3. 当前桥接层只处理会话输入输出，不承担几何求解。
 *
 * 文件职责：
 * 1. 从 prototype 恢复圆角编辑定义；
 * 2. 构建 preview / commit 所需的特征输入；
 * 3. 为 web-client 提供稳定的圆角编辑会话载荷。
 */

import { createFeatureReferenceBinding } from '../../../utils/feature/createFeatureReferenceBinding';
import type { FeatureReferenceBinding } from '../../binding/FeatureReferenceBinding';
import { FilletFeatureDefinitionModel } from '../../definition/fillet/FilletFeatureDefinition';
import type { FeatureEditSession } from '../FeatureEditSession';
import { buildFilletModelTopologyBindings } from '../../binding/buildFilletModelTopologyBindings';
import type { FilletFeaturePrototype } from '../../prototype/fillet/FilletFeaturePrototype';

/**
 * 圆角编辑定义。
 */
export type FilletFeatureEditDefinition = FilletFeatureDefinition;

/**
 * 圆角编辑会话载荷。
 */
export interface FilletEditSessionPayload {
    /** 当前编辑会话。 */
    session: FeatureEditSession | null;
    /** 当前圆角定义。 */
    definition: FilletFeatureEditDefinition | null;
    /** 上游实体对象 ID。 */
    upstreamBodyObjectId: string | null;
    /** 特征名称。 */
    name: string | null;
}

/**
 * 圆角特征会话桥接层。
 */
export class FilletFeatureSessionBridge {
    /**
     * 从 prototype 恢复编辑定义。
     *
     * @param prototype 圆角原型。
     * @returns 编辑定义。
     */
    public restoreDefinitionFromPrototype(prototype: FilletFeaturePrototype): FilletFeatureEditDefinition {
        return structuredClone(prototype.getDefinition().getBlendDefinition());
    }

    /**
     * 构建预览输入。
     *
     * @param input 输入参数。
     * @returns 预览执行载荷。
     */
    public buildPreviewInput(input: {
        featureId?: string | null;
        prototype: FilletFeaturePrototype;
        definition: FilletFeatureEditDefinition;
    }): {
        featureKind: 'fillet';
        featureId?: string | null;
        definition: FilletFeatureDefinitionModel;
        bindings: FeatureReferenceBinding[];
    } {
        return this.buildExecutionInput(input);
    }

    /**
     * 构建提交输入。
     *
     * @param input 输入参数。
     * @returns 提交执行载荷。
     */
    public buildCommitInput(input: {
        featureId?: string | null;
        prototype: FilletFeaturePrototype;
        definition: FilletFeatureEditDefinition;
    }): {
        featureKind: 'fillet';
        featureId?: string | null;
        definition: FilletFeatureDefinitionModel;
        bindings: FeatureReferenceBinding[];
    } {
        return this.buildExecutionInput(input);
    }

    /**
     * 构建宿主侧编辑会话载荷。
     *
     * @param session 编辑会话。
     * @returns 宿主可消费的载荷。
     */
    public buildEditSessionPayload(session: FeatureEditSession | null): FilletEditSessionPayload {
        if (!session) {
            return {
                session: null,
                definition: null,
                upstreamBodyObjectId: null,
                name: null,
            };
        }

        const prototype = session.getPrototype() as FilletFeaturePrototype;
        const projection = prototype.getDefinition().toProjection();
        return {
            session,
            definition: this.restoreDefinitionFromPrototype(prototype),
            upstreamBodyObjectId: projection.upstreamBodyObjectId,
            name: projection.name,
        };
    }

    /**
     * 统一构建执行输入。
     *
     * @param input 输入参数。
     * @returns 执行载荷。
     */
    private buildExecutionInput(input: {
        featureId?: string | null;
        prototype: FilletFeaturePrototype;
        definition: FilletFeatureEditDefinition;
    }): {
        featureKind: 'fillet';
        featureId?: string | null;
        definition: FilletFeatureDefinitionModel;
        bindings: FeatureReferenceBinding[];
    } {
        const baseCommandParams = input.prototype.getDefinition().getCommandParams();
        const commandParams: FilletParams = {
            ...structuredClone(baseCommandParams),
            featureKind: 'fillet',
            blendMode: 'fillet',
            definition: structuredClone(input.definition),
            editingFeatureId: input.featureId ?? undefined,
            referenceBindings: buildFilletModelTopologyBindings({
                upstreamBodyObjectId: baseCommandParams.upstreamBodyObjectId,
                definition: input.definition,
            }),
        };

        return {
            featureKind: 'fillet',
            featureId: input.featureId,
            definition: FilletFeatureDefinitionModel.fromParams({
                commandParams,
            }),
            bindings: (commandParams.referenceBindings ?? []).map((binding) => createFeatureReferenceBinding(binding)),
        };
    }
}
